import apiClient from "../../client/apiClient";
import { appendQueryParams } from "../../utils";

// Which subjects a class takes in a session (RMS `class_subjects`).
// Distinct from course-structure, which is the per-exam marks allocation:
// this says "Grade V studies these ten subjects", that says "for the First
// Term, Maths is out of 100".

export interface ClassSubject {
  _id: string;
  session: { _id: string; title: string; branch?: string };
  class: { _id: string; name: string; stage?: string; system?: string; order?: number };
  classGroup: { _id: string; name: string } | null;
  course: {
    _id: string;
    title: string;
    courseIdentifier: string;
    displayOrder?: number;
  };
  displayOrder: number;
}

const ENDPOINT = {
  BASE: "/class-subject",
  BULK: "/class-subject/bulk",
  GROUP: "/class-subject/group",
  SYNC: "/class-subject/sync-from-marks-allocation",
};

/**
 * List the subject rows. The caller groups them into class cards.
 * Omitting `session` returns every session of the campus in scope;
 * `all: true` lifts that for owner/admin.
 */
export const getAllClassSubjects = async (
  params: {
    session?: string;
    class?: string;
    classGroup?: string | null;
    all?: boolean;
  } = {}
): Promise<{ success: boolean; data: ClassSubject[] }> => {
  const url = appendQueryParams(ENDPOINT.BASE, {
    ...(params.session ? { session: params.session } : {}),
    ...(params.class ? { class: params.class } : {}),
    ...(params.classGroup !== undefined && params.classGroup !== null
      ? { classGroup: params.classGroup }
      : {}),
    ...(params.all ? { all: "true" } : {}),
  });
  const response = await apiClient({ url, method: "GET" });
  return response.data;
};

/**
 * Replace the whole subject set for one class (+ optional group) in a session.
 * Sending fewer subjects than before REMOVES the rest, and that removal
 * cascades server-side to their marks allocation and teaching assignments.
 * `courses` order becomes the display order.
 */
export const setClassSubjects = async (data: {
  session: string;
  class: string;
  classGroup?: string | null;
  courses: string[];
}): Promise<{
  success: boolean;
  added: number;
  removed: number;
  cascaded: { courseStructures: number; teacherAssignments: number };
}> => {
  const response = await apiClient({
    url: ENDPOINT.BULK,
    method: "PUT",
    data,
  });
  return response.data;
};

/**
 * Backfill from marks allocation: any subject already configured there but not
 * ticked here gets added. INSERT ONLY — it never removes, so it is safe to run
 * repeatedly and cannot undo a deliberate un-ticking.
 */
export const syncClassSubjectsFromMarksAllocation = async (
  params: { all?: boolean } = {}
): Promise<{
  success: boolean;
  inserted: number;
  classes: number;
  message: string;
}> => {
  const url = appendQueryParams(ENDPOINT.SYNC, params.all ? { all: "true" } : {});
  const response = await apiClient({ url, method: "POST" });
  return response.data;
};

/**
 * Remove an entire class card — every subject for that session+class+group.
 * Cascades the same way as removing them one by one.
 */
export const deleteClassSubjectGroup = async (params: {
  session: string;
  class: string;
  classGroup?: string | null;
}): Promise<{ success: boolean }> => {
  const url = appendQueryParams(ENDPOINT.GROUP, {
    session: params.session,
    class: params.class,
    ...(params.classGroup ? { classGroup: params.classGroup } : {}),
  });
  const response = await apiClient({ url, method: "DELETE" });
  return response.data;
};
